package org.example.project.game

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.database.database
import kotlinx.coroutines.flow.first

/**
 * LevelLoader — laedt ein Level genau wie die Android-App.
 *
 * Pfad: /games/{gameId}
 * Struktur (FirebaseLevel + LangPack pro Sprache, siehe deine GameModels.kt)
 *
 * Wird vom MultiplayerGameViewModel benutzt, sobald per Listener ein
 * neues 'gameId' im Raum erscheint. Der Host laedt das Level beim
 * Erstellen aus einer Liste (siehe MultiplayerLobbyViewModel).
 */
class LevelLoader {
    private val db = Firebase.database("https://assoziation-c5cda-default-rtdb.firebaseio.com")
    private val gamesRef = db.reference("games")

    /**
     * Laedt ein konkretes Level per ID.
     */
    suspend fun loadLevel(gameId: String, langCode: String): LangPack? {
        val snapshot = gamesRef.child(gameId).valueEvents.first()
        if (!snapshot.exists) return null
        val level = try {
            snapshot.value(FirebaseLevel.serializer())
        } catch (_: Exception) {
            return null
        }
        return level.translations[langCode.uppercase()]
            ?: level.translations["DE"]
            ?: level.translations.values.firstOrNull()
    }

    /**
     * Sucht zufaelliges Level mit passender Difficulty. Gibt (gameId, LangPack) zurueck.
     * Wird vom Host beim Raum-Erstellen benutzt.
     */
    suspend fun fetchRandomLevel(langCode: String, difficulty: String): Pair<String, LangPack>? {
        val snapshot = gamesRef.valueEvents.first()
        if (!snapshot.exists) return null

        val targetLang = langCode.uppercase()

        // Erst nach Difficulty UND vorhandener Sprache filtern,
        // dann Fallback nur Sprache, dann irgendwas
        val byDiffAndLang = snapshot.children.mapNotNull { child ->
            val level = try { child.value(FirebaseLevel.serializer()) } catch (_: Exception) { return@mapNotNull null }
            val id = child.key ?: return@mapNotNull null
            if (level.difficulty == difficulty && level.translations.containsKey(targetLang)) {
                id to level
            } else null
        }

        val pool = byDiffAndLang.ifEmpty {
            snapshot.children.mapNotNull { child ->
                val level = try { child.value(FirebaseLevel.serializer()) } catch (_: Exception) { return@mapNotNull null }
                val id = child.key ?: return@mapNotNull null
                if (level.translations.containsKey(targetLang)) id to level else null
            }
        }

        if (pool.isEmpty()) return null
        val (gameId, level) = pool.random()
        val pack = level.translations[targetLang]
            ?: level.translations["DE"]
            ?: level.translations.values.firstOrNull()
            ?: return null
        return gameId to pack
    }
}